import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { api } from '@/lib/api';

export default function SuperUsersPage() {
  const q = useQuery({
    queryKey: ['super-users'],
    queryFn: async () => {
      const r = await api('/api/v1/super/user-activity');
      if (r.status !== 200) throw new Error('users');
      return r.data;
    },
  });

  const columns = useMemo(
    () => [
      { key: 'displayName', label: 'Name', sortable: true },
      { key: 'email', label: 'Email' },
      {
        key: 'role',
        label: 'Role',
        render: (v) => <StatusPill status={v} />,
      },
      { key: 'companyId', label: 'Company', className: 'font-mono text-xs' },
      {
        key: 'status',
        label: 'Status',
        render: (v) => <StatusPill status={v} />,
      },
      {
        key: 'lastLoginAt',
        label: 'Last login',
        render: (v) => (v ? new Date(String(v)).toLocaleString() : '—'),
      },
    ],
    []
  );

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="User activity"
          description="End-user directory and auth action volume across tenants."
          icon="Users"
          metrics={[
            { label: 'Users', value: q.data?.users?.length ?? 0 },
            { label: 'Auth events', value: q.data?.authLogs?.length ?? 0 },
          ]}
        />

        <Card>
          <CardHeader>
            <CardTitle>Top actors</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <th className="pb-2">User</th>
                  <th className="pb-2">Company</th>
                  <th className="pb-2 text-right">Actions</th>
                  <th className="pb-2">Last</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(q.data?.rolls ?? []).slice(0, 15).map((r) => (
                  <tr key={r.userId}>
                    <td className="py-2 font-mono text-xs">{r.userId.slice(0, 10)}…</td>
                    <td className="py-2 font-mono text-xs">{r.companyId.slice(0, 10)}</td>
                    <td className="py-2 text-right font-mono data-text">{r.actions}</td>
                    <td className="py-2">{new Date(r.lastAt).toLocaleString()}</td>
                  </tr>
                ))}
                {!q.data?.rolls?.length && (
                  <tr>
                    <td colSpan={4} className="py-4 text-muted-foreground">
                      No activity yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <DataTable
              columns={columns}
              data={q.data?.users ?? []}
              emptyMessage="No users"
            />
          </CardContent>
        </Card>
      </div>
);
}
